import { useEffect, useState } from "react";
import { api, FILE_URL } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Check, X, PackageCheck } from "lucide-react";

export default function BrandModeration() {
  const [pending, setPending] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [p, r] = await Promise.all([
        api.get("/moderation/brand-products", { params: { status: "pending" } }),
        api.get("/moderation/try-requests", { params: { status: "requested" } }),
      ]);
      setPending(p.data);
      setRequests(r.data);
    } catch (e) {
      toast(e?.response?.data?.detail || "Could not load queues.");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const approve = async (id) => {
    try { await api.post(`/moderation/brand-products/${id}/approve`); setPending((p) => p.filter((x) => x.id !== id)); toast("Product approved."); } catch { toast("Could not approve."); }
  };
  const reject = async (id) => {
    const reason = window.prompt("Reason for rejection (optional)");
    try { await api.post(`/moderation/brand-products/${id}/reject`, { reason: reason || null }); setPending((p) => p.filter((x) => x.id !== id)); toast("Product rejected."); } catch { toast("Could not reject."); }
  };
  const fulfill = async (id) => {
    try { await api.post(`/moderation/try-requests/${id}/fulfill`); setRequests((r) => r.filter((x) => x.id !== id)); toast("Marked fulfilled — they can now review."); } catch { toast("Could not fulfill."); }
  };
  const decline = async (id) => {
    const reason = window.prompt("Reason for decline (optional)");
    try { await api.post(`/moderation/try-requests/${id}/decline`, { reason: reason || null }); setRequests((r) => r.filter((x) => x.id !== id)); toast("Request declined."); } catch { toast("Could not decline."); }
  };

  return (
    <div className="mt-14 space-y-14">
      <section>
        <h2 className="font-serif-display text-3xl text-[#2A2823]">Try &amp; Critique — brand applications</h2>
        <p className="text-sm text-[#2A2823]/60 mt-1">Only approved products appear in the Try &amp; Critique pool. They never enter The Grove.</p>
        {loading ? (
          <p className="mt-6 text-[#2A2823]/50">Loading…</p>
        ) : pending.length === 0 ? (
          <p data-testid="mod-brands-empty" className="mt-6 text-[#2A2823]/60 italic">No pending applications.</p>
        ) : (
          <div className="mt-6 space-y-4" data-testid="mod-brands">
            {pending.map((p) => (
              <article key={p.id} data-testid={`mod-brand-${p.id}`} className="rounded-2xl border border-[#B79256]/25 bg-white p-4 sm:p-5 flex flex-col sm:flex-row gap-4">
                <img src={FILE_URL(p.photo_path)} alt="" className="w-24 h-24 rounded-2xl object-cover border border-[#B79256]/20 flex-shrink-0"/>
                <div className="flex-1 min-w-0">
                  <div className="accent-label text-[#B79256]">{p.category} · {p.submitted_by_email || "no email"}</div>
                  <div className="font-serif-display text-xl text-[#2A2823] leading-tight mt-1">{p.brand_name} — {p.product_name}</div>
                  <p className="text-sm text-[#2A2823]/75 mt-2 line-clamp-3">{p.description}</p>
                  {p.website && <a href={p.website} target="_blank" rel="noopener noreferrer" className="text-xs text-[#4E5A3D] underline mt-1 inline-block">{p.website}</a>}
                </div>
                <div className="flex sm:flex-col gap-2">
                  <Button data-testid={`mod-approve-${p.id}`} onClick={() => approve(p.id)} className="rounded-full h-10 px-4 bg-[#4E5A3D] text-[#FBF8F1] hover:bg-[#3E4830] shadow-none">
                    <Check className="w-4 h-4 mr-1" strokeWidth={1.75}/> Approve
                  </Button>
                  <Button data-testid={`mod-reject-${p.id}`} onClick={() => reject(p.id)} variant="outline" className="rounded-full h-10 px-4 border-[#B79256]/40 bg-transparent text-[#2A2823] hover:bg-[#B79256]/10 hover:text-[#2A2823]">
                    <X className="w-4 h-4 mr-1" strokeWidth={1.75}/> Reject
                  </Button>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      <section>
        <h2 className="font-serif-display text-3xl text-[#2A2823]">Try &amp; Critique — requests to try</h2>
        <p className="text-sm text-[#2A2823]/60 mt-1">Mark fulfilled once the product is shipped so the recipient can plant an honest branch.</p>
        {requests.length === 0 ? (
          <p data-testid="mod-requests-empty" className="mt-6 text-[#2A2823]/60 italic">No pending requests.</p>
        ) : (
          <div className="mt-6 space-y-4" data-testid="mod-requests">
            {requests.map((r) => (
              <article key={r.id} data-testid={`mod-req-${r.id}`} className="rounded-2xl border border-[#B79256]/25 bg-white p-4 sm:p-5 flex flex-col sm:flex-row gap-4">
                <div className="flex-1 min-w-0">
                  <div className="accent-label text-[#B79256]">{r.product ? r.product.brand_name : "unknown brand"}</div>
                  <div className="font-serif-display text-xl text-[#2A2823] leading-tight mt-1">{r.user_name} · {r.product ? r.product.product_name : "product"}</div>
                  {r.message && <p className="text-sm text-[#2A2823]/75 mt-2 italic">"{r.message}"</p>}
                </div>
                <div className="flex sm:flex-col gap-2">
                  <Button data-testid={`mod-fulfill-${r.id}`} onClick={() => fulfill(r.id)} className="rounded-full h-10 px-4 bg-[#4E5A3D] text-[#FBF8F1] hover:bg-[#3E4830] shadow-none">
                    <PackageCheck className="w-4 h-4 mr-1" strokeWidth={1.75}/> Fulfilled
                  </Button>
                  <Button data-testid={`mod-decline-${r.id}`} onClick={() => decline(r.id)} variant="outline" className="rounded-full h-10 px-4 border-[#B79256]/40 bg-transparent text-[#2A2823] hover:bg-[#B79256]/10 hover:text-[#2A2823]">
                    <X className="w-4 h-4 mr-1" strokeWidth={1.75}/> Decline
                  </Button>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
